// Repairs rich-text that Word (and older saves from this editor) produced, so the
// Training typography actually applies to it.
//
// Two classes of damage are handled:
//
// 1. Structure. Word emits each list item as its own paragraph and relies on its
//    own numbering metadata; converting that to HTML routinely lands a nested
//    <ol>/<ul> as a *sibling* of the <li> it belongs under, rather than inside it.
//    A sibling sub-list is a new list to the browser, so it restarts at 1 and sits
//    at the wrong indent. Consecutive sibling lists at the same level have the same
//    effect on the top-level numbering.
//
// 2. Presentation. Pasted spans and style attributes carry Word's own font,
//    point size and line spacing, which override the lesson stylesheet and leave
//    one section looking unlike every other.
//
// Both run over a detached DOM, so the document being viewed is never touched.

const LIST_TAGS = ['OL', 'UL'];

// Elements that already end the line, so a sub-list moved in after one does not
// need a wrapper to keep the text on separate lines.
const BLOCK_TAGS = {
    P: true, DIV: true, H1: true, H2: true, H3: true, H4: true, H5: true, H6: true,
    BLOCKQUOTE: true, PRE: true, TABLE: true, HR: true, BR: true, FIGURE: true,
};

// Layout properties Word attaches to every paragraph. These fight the lesson
// stylesheet and are never set deliberately from the toolbar, so they go on both
// paste and render. Colour and text alignment are left alone: real choices.
const STRIPPED_STYLE_PROPS = [
    'margin',
    'margin-top',
    'margin-bottom',
    'margin-left',
    'margin-right',
    'text-indent',
    'letter-spacing',
    'word-spacing',
    'mso-ansi-font-size',
];

// Font and size are different: the toolbar sets exactly these, so stripping them
// everywhere would undo the author's own choice (it did — the font dropdown
// appeared to do nothing). They are removed only from a paste, where they are
// Word's defaults rather than anything anyone picked.
const PASTE_ONLY_STYLE_PROPS = [
    'font-family',
    'font-size',
    'line-height',
    'font',
];

function isList(node) {
    return !!node && node.nodeType === 1 && LIST_TAGS.indexOf(node.tagName) !== -1;
}

// True when the <li> ends in loose inline content (text or a <strong>/<em>/…)
// rather than in a block that already closes the line.
function hasTrailingInlineText(li) {
    for (let i = li.childNodes.length - 1; i >= 0; i--) {
        const node = li.childNodes[i];
        if (node.nodeType === 3) {
            if (node.textContent.replace(/ /g, ' ').trim()) return true;
            continue;
        }
        if (node.nodeType !== 1) continue;
        if (isList(node)) return false;
        return !BLOCK_TAGS[node.tagName];
    }
    return false;
}

// Wrap an <li>'s loose inline content in a <p> so a sub-list appended after it
// starts on its own line, exactly as it read before the repair.
function wrapInlineChildren(li) {
    const paragraph = li.ownerDocument.createElement('p');
    while (li.firstChild && !isList(li.firstChild)) {
        paragraph.appendChild(li.firstChild);
    }
    if (paragraph.childNodes.length) li.insertBefore(paragraph, li.firstChild);
}

// A sub-list that ended up as a direct child of a list belongs inside the <li>
// that precedes it. Moving it there restores both the indent and the parent's
// continuous numbering.
function reparentStrayLists(root) {
    LIST_TAGS.forEach(tag => {
        Array.from(root.getElementsByTagName(tag)).forEach(list => {
            const parent = list.parentNode;
            if (!isList(parent)) return;

            let previous = list.previousSibling;
            while (previous && !(previous.nodeType === 1 && previous.tagName === 'LI')) {
                previous = previous.previousSibling;
            }

            if (previous) {
                // The stray list was a separate block, so its text did not run on
                // from the item above. Appending it directly would fuse the last
                // word of the <li> to the first word of the sub-list
                // ("…advice." + "Seller…"); the <li>'s own text needs to stay a
                // block of its own.
                if (hasTrailingInlineText(previous)) wrapInlineChildren(previous);
                previous.appendChild(list);
                return;
            }

            // No <li> to attach to: give the sub-list one so it is still valid
            // markup and keeps its indent instead of collapsing to the margin.
            const host = root.ownerDocument.createElement('li');
            parent.insertBefore(host, list);
            host.appendChild(list);
        });
    });
}

// Word splits what the author sees as one numbered list into several lists —
// around an interrupting paragraph, or for no visible reason at all. Each one
// restarts at 1. Merging adjacent same-type lists restores a single sequence.
function mergeAdjacentLists(root) {
    let merged = false;
    LIST_TAGS.forEach(tag => {
        Array.from(root.getElementsByTagName(tag)).forEach(list => {
            if (!list.parentNode) return;

            let next = list.nextSibling;
            while (next) {
                // Whitespace between two lists is layout noise, not content.
                const isBlankText = next.nodeType === 3 && !next.textContent.trim();
                if (!isBlankText) break;
                const blank = next;
                next = next.nextSibling;
                blank.parentNode.removeChild(blank);
            }

            // An explicit start= is an author continuing a sequence on purpose;
            // merging would silently renumber it.
            if (!next || next.nodeType !== 1 || next.tagName !== tag) return;
            if (next.hasAttribute('start')) return;

            while (next.firstChild) list.appendChild(next.firstChild);
            next.parentNode.removeChild(next);
            merged = true;
        });
    });

    return merged;
}

// A numbered sequence is often broken into several <ol>s by a paragraph between
// the items — a preamble, a signature line, a "check one" block. Each later list
// starts again at 1, so a contract numbered 1..14 reads 1,2,3 then 1,2,3,4 then
// 1. The lists cannot simply be merged: the paragraphs between them are content
// and must stay where they are. Instead each continuation is given the number it
// should start on, which is what Word's own numbering does across such breaks.
//
// Only top-level ordered lists are chained, and only where the run is
// uninterrupted by a heading — a heading marks a genuinely new sequence.
function continueBrokenNumbering(root) {
    // Lists are not always children of `root` — a paste arrives inside TinyMCE's
    // own <div>, and Word adds wrappers of its own — so chain within each parent
    // that holds top-level lists, not just root.children.
    const hosts = [];
    Array.from(root.getElementsByTagName('ol')).forEach(ol => {
        if (ol.closest('li')) return;
        if (hosts.indexOf(ol.parentNode) === -1) hosts.push(ol.parentNode);
    });

    hosts.forEach(host => chainWithin(host));
}

function chainWithin(root) {
    // One left-to-right pass: walk the top-level children, tracking how many
    // items the current run has used. A heading ends the run; so does an
    // author's explicit start=, which is a deliberate number we must not move.
    let used = 0;

    Array.from(root.children).forEach(node => {
        if (node.nodeType !== 1) return;

        if (/^H[1-6]$/.test(node.tagName)) {
            used = 0;
            return;
        }

        if (node.tagName !== 'OL') return;

        // Browsers number from 1 without being told, so a list that says
        // start="1" is saying it is a sequence of its own — every list rebuilt
        // from a Word paste declares its start for exactly this reason.
        // A start= that would step backwards is a stale hand-patch from before
        // the run was repaired (this content carried a start="9" landing after
        // item 16). Renumber it; a start= that still moves forward is honoured.
        if (node.hasAttribute('start')) {
            const declared = parseInt(node.getAttribute('start'), 10);
            if (declared === 1) {
                used = node.children.length;
                return;
            }
            if (!isNaN(declared) && declared > used) {
                used = declared - 1 + node.children.length;
                return;
            }
        }

        if (used > 0) node.setAttribute('start', String(used + 1));
        else node.removeAttribute('start');
        used += node.children.length;
    });
}

// An empty <li> is what a stray <br> or a trailing Word paragraph mark becomes.
// Left in place it consumes a number, so the visible list skips one. Dropping
// items can leave a list with nothing in it, and dropping that list can leave
// the item that held it empty in turn, so this runs until nothing changes.
function dropEmptyListItems(root) {
    let changed = true;
    while (changed) {
        changed = false;

        Array.from(root.getElementsByTagName('li')).forEach(li => {
            if (li.getElementsByTagName('*').length) return;
            const text = (li.textContent || '').replace(/ /g, ' ').trim();
            if (text) return;
            li.parentNode.removeChild(li);
            changed = true;
        });

        LIST_TAGS.forEach(tag => {
            Array.from(root.getElementsByTagName(tag)).forEach(list => {
                if (list.querySelector('li')) return;
                list.parentNode.removeChild(list);
                changed = true;
            });
        });
    }
}

function stripPresentationStyles(root, fromPaste) {
    const props = fromPaste
        ? STRIPPED_STYLE_PROPS.concat(PASTE_ONLY_STYLE_PROPS)
        : STRIPPED_STYLE_PROPS;

    Array.from(root.querySelectorAll('[style]')).forEach(el => {
        props.forEach(prop => el.style.removeProperty(prop));
        if (!el.getAttribute('style')) el.removeAttribute('style');
    });

    // <font face>/<size> is only ever legacy or pasted markup, never the toolbar.
    Array.from(root.querySelectorAll('[face], [size]')).forEach(el => {
        el.removeAttribute('face');
        el.removeAttribute('size');
    });

    // Word marks its own paragraph/list classes (MsoListParagraph, MsoNormal…);
    // they reference a stylesheet that never travels with the paste.
    Array.from(root.querySelectorAll('[class]')).forEach(el => {
        const kept = (el.getAttribute('class') || '')
            .split(/\s+/)
            .filter(c => c && !/^Mso/i.test(c));
        if (kept.length) el.setAttribute('class', kept.join(' '));
        else el.removeAttribute('class');
    });
}

// A <span> that carried only Word's font is pure noise once the style is gone.
function unwrapBareSpans(root) {
    Array.from(root.getElementsByTagName('span')).forEach(span => {
        if (span.attributes.length) return;
        const parent = span.parentNode;
        if (!parent) return;
        while (span.firstChild) parent.insertBefore(span.firstChild, span);
        parent.removeChild(span);
    });
}

// `options.fromPaste` marks content arriving from the clipboard, where a font or
// size is Word's default rather than a choice made here — see
// PASTE_ONLY_STYLE_PROPS. On save and render it is absent, so a font set from
// the toolbar is preserved.
export function normalizeContentHtml(html, options) {
    if (!html || typeof html !== 'string') return html || '';
    if (typeof document === 'undefined') return html;

    const root = document.createElement('div');
    root.innerHTML = html;

    stripPresentationStyles(root, !!(options && options.fromPaste));
    unwrapBareSpans(root);
    dropEmptyListItems(root);

    // The two list passes feed each other: reparenting a stray list can leave two
    // sub-lists adjacent inside one <li>, and merging those can expose a further
    // stray. Repeat until nothing changes so the result is the same whether the
    // content is normalized once on save or again on every render. The bound is a
    // guard against pathological markup, not an expected path — real lessons
    // settle in two rounds.
    for (let pass = 0; pass < 10; pass++) {
        reparentStrayLists(root);
        if (!mergeAdjacentLists(root)) break;
    }

    // After the structure settles, chain any numbered run that a paragraph split
    // into separate lists, so a contract numbered 1..14 does not restart at 1.
    continueBrokenNumbering(root);

    return root.innerHTML;
}

export default normalizeContentHtml;
